import { memo, useState, useRef, useCallback, useEffect } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { SceneVideoThumbnail } from './SceneVideoThumbnail';
import { SceneCardHeader } from './PropertySceneCardHeader';
import { SceneThumbnail } from './PropertySceneCardShared';
import { HorizontalLayout } from './PropertySceneCardHorizontal';
import { VerticalVoiceoverSection } from './PropertySceneCardVerticalVoiceover';
import { VerticalPromptSection } from './PropertySceneCardVerticalPrompts';
import { PromptTemplateSelectMemo } from './PromptTemplateSelect';
import type { IPropertySceneCardProps } from '@/@types/propertySceneEditor';
import type { IPromptTemplateBasic } from '@/@types/propertySceneCard';
import type { Id } from '../../../convex/_generated/dataModel';
import type { PromptTemplateImageType } from '@/@types/promptTemplates';

function getEffectiveSourceImage(
  scene: IPropertySceneCardProps['scene']
): Id<'_storage'> | undefined {
  return scene.selectedImageId ?? scene.sourceImageId;
}

function VerticalLayout({
  scene,
  dragHandleProps,
  aspectRatio,
  isSaving,
  isRegeneratingVoiceover,
  isRegeneratingVideo,
  isDeleting,
  isPlayingAudio,
  onPlayPause,
  onVoiceoverTextChange,
  onImageSelect,
  onPromptEdit,
  onImagePromptChange,
  onRegenerateVideo,
  onExpandVideo,
  onDelete,
  onTemplateChange,
  onNegativePromptChange,
  onRegenerateVoiceover,
}: IPropertySceneCardProps & { isPlayingAudio: boolean; onPlayPause: () => void }) {
  const effectiveImageId = getEffectiveSourceImage(scene);

  const template = useQuery(
    api.property.promptTemplates.getUnified,
    scene.promptTemplateId ? { templateId: scene.promptTemplateId } : 'skip'
  ) as IPromptTemplateBasic | null | undefined;

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      <SceneCardHeader
        sceneNumber={scene.sceneNumber}
        sceneType={scene.sceneType}
        isDeleting={isDeleting}
        onDelete={onDelete}
        dragHandleProps={dragHandleProps}
      />

      <div className="p-4 space-y-4">
        <div className="flex gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-muted-foreground">Source Image</span>
            <SceneThumbnail
              storageId={effectiveImageId}
              sceneType={scene.sceneType}
              onChangeClick={onImageSelect}
              size="large"
              aspectRatio={aspectRatio}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-muted-foreground">Generated Video</span>
            <SceneVideoThumbnail
              videoStorageId={scene.generatedVideoId}
              status={scene.imageToVideoStatus || 'pending'}
              aspectRatio={aspectRatio}
              size="large"
              hasExistingVideo={!!scene.generatedVideoId}
              isRegenerating={isRegeneratingVideo}
              onPlay={() => {}}
              onExpand={onExpandVideo}
              onRegenerate={onRegenerateVideo}
            />
          </div>
          <VerticalVoiceoverSection
            sceneId={scene._id}
            scene={scene}
            isSaving={isSaving}
            isRegeneratingVoiceover={isRegeneratingVoiceover}
            isPlayingAudio={isPlayingAudio}
            onPlayPause={onPlayPause}
            onRegenerateVoiceover={onRegenerateVoiceover}
            onVoiceoverTextChange={onVoiceoverTextChange}
          />
        </div>

        <div className="border-t pt-4">
          <label
            htmlFor={`scene-video-style-${scene._id}`}
            className="text-sm font-medium mb-2 block"
          >
            Video Style:
          </label>
          <PromptTemplateSelectMemo
            triggerId={`scene-video-style-${scene._id}`}
            value={scene.promptTemplateId ?? null}
            onChange={templateId => onTemplateChange?.(scene._id, templateId)}
            imageType={scene.sceneType as PromptTemplateImageType}
            disabled={isSaving}
          />
        </div>

        <VerticalPromptSection
          sceneId={scene._id}
          scene={scene}
          template={template}
          isSaving={isSaving}
          onPromptEdit={onPromptEdit}
          onImagePromptChange={onImagePromptChange}
          onNegativePromptChange={onNegativePromptChange}
        />
      </div>
    </div>
  );
}

function PropertySceneCard(props: IPropertySceneCardProps) {
  const { scene, layout, onPlayVoiceover } = props;
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const voiceoverUrl = useQuery(
    api.storage.getUrl,
    scene.voiceoverAudioId ? { storageId: scene.voiceoverAudioId } : 'skip'
  );

  const handlePlayPause = useCallback(() => {
    if (!voiceoverUrl) return;

    if (!audioRef.current) {
      audioRef.current = new Audio(voiceoverUrl);
      audioRef.current.addEventListener('ended', () => {
        setIsPlayingAudio(false);
      });
      audioRef.current.addEventListener('error', () => {
        setIsPlayingAudio(false);
      });
    }

    if (isPlayingAudio) {
      audioRef.current.pause();
      setIsPlayingAudio(false);
    } else {
      audioRef.current.play().catch(() => {
        setIsPlayingAudio(false);
      });
      setIsPlayingAudio(true);
      onPlayVoiceover();
    }
  }, [voiceoverUrl, isPlayingAudio, onPlayVoiceover]);

  if (layout === 'horizontal') {
    return (
      <HorizontalLayout {...props} isPlayingAudio={isPlayingAudio} onPlayPause={handlePlayPause} />
    );
  }

  return (
    <VerticalLayout {...props} isPlayingAudio={isPlayingAudio} onPlayPause={handlePlayPause} />
  );
}

export const PropertySceneCardMemo = memo(PropertySceneCard);
